import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import LoadingIndicator from '../common/LoadingIndicator';
import { configurationKeys } from '../constants/clientConstants';
import { LoadingStates as states } from '../constants/states';
import { getAllAuthFactors } from '../services/walletService';

const ChevronRight = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="vf-method-chevron shrink-0"
    aria-hidden="true"
  >
    <path d="m9 18 6-6-6-6" />
  </svg>
);

const resolveAssetUrl = (path) =>
  `${process.env.PUBLIC_URL || ''}${path.startsWith('/') ? path : `/${path}`}`;

/** Meta from design.fayda.et/verifayda-v5 method list */
const METHOD_META = {
  otp: {
    badgeKey: 'badge_fastest',
    badge: 'Fastest',
    badgeClass: 'vf-badge-fastest',
    titleKey: 'login_with_otp_title',
    titleDefault: 'Login with Code (OTP)',
    descKey: 'login_with_otp_desc',
    descDefault: '6-digit one-time code via SMS or Email',
  },
  bio: {
    badgeKey: 'badge_most_secure',
    badge: 'Most Secure',
    badgeClass: 'vf-badge-secure',
    titleKey: 'login_with_bio_title',
    titleDefault: 'Login with Biometrics',
    descKey: 'login_with_bio_desc',
    descDefault: 'Fingerprint, face, or iris scan using connected device',
  },
  totp: {
    badgeKey: 'badge_offline_ready',
    badge: 'Offline Ready',
    badgeClass: 'vf-badge-offline',
    titleKey: 'login_with_totp_title',
    titleDefault: 'Login with Faydapass (TOTP)',
    descKey: 'login_with_totp_desc',
    descDefault: 'Time-based 6-digit code or QR scan via your app',
  },
};

function methodKind(option) {
  const id = (option?.id || '').toLowerCase();
  const label = (option?.label || '').toLowerCase();
  // Check totp before otp — "totp" contains the substring "otp"
  if (id.includes('totp') || label.includes('totp') || label.includes('fayda'))
    return 'totp';
  if (id.includes('bio') || label.includes('bio')) return 'bio';
  if (id.includes('otp') || label.includes('otp') || label.includes('code'))
    return 'otp';
  return null;
}

export default function SignInOptions({
  openIDConnectService,
  handleSignInOptionClick,
  i18nKeyPrefix = 'signInOption',
  icons,
  authLabel,
}) {
  const { t } = useTranslation('translation', { keyPrefix: i18nKeyPrefix });

  const [status, setStatus] = useState({ state: states.LOADED, msg: '' });
  const [singinOptions, setSinginOptions] = useState(null);
  const [showMoreOptions, setShowMoreOptions] = useState(false);

  useEffect(() => {
    setStatus({ state: states.LOADING, msg: 'loading_msg' });

    let oAuthDetails = openIDConnectService.getOAuthDetails();
    let authFactors = oAuthDetails?.authFactors;

    let wlaList =
      openIDConnectService.getEsignetConfiguration(
        configurationKeys.walletConfig
      ) ?? process.env.REACT_APP_WALLET_CONFIG;

    let loginOptions = getAllAuthFactors(authFactors, wlaList);

    if (loginOptions.length === 1) {
      handleSignInOptionClick(loginOptions[0].value, icons, authLabel);
    }

    setSinginOptions(loginOptions);
    setShowMoreOptions(loginOptions.length > 4 && loginOptions.length !== 5);
    setStatus({ state: states.LOADED, msg: '' });
  }, []);

  return (
    <div className="vf-methods text-white">
      <div className="vf-methods-header">
        <h2 className="vf-methods-title">
          {t('choose_verification_method', {
            defaultValue: 'Choose Verification Method',
          })}
        </h2>
      </div>

      {status.state === states.LOADING && (
        <div>
          <LoadingIndicator size="medium" message={status.msg} />
        </div>
      )}

      {status.state === states.LOADED && singinOptions && (
        <div className="vf-methods-list flex flex-col gap-4">
          {singinOptions
            .slice(0, showMoreOptions ? 4 : undefined)
            .map((option, idx) => {
              const kind = methodKind(option);
              const meta = kind ? METHOD_META[kind] : null;
              const title = meta
                ? t(meta.titleKey, { defaultValue: meta.titleDefault })
                : t(authLabel || 'login_with', {
                    option: t(option.label, option.label),
                  });
              const desc = meta
                ? t(meta.descKey, { defaultValue: meta.descDefault })
                : '';

              return (
                <div key={idx} className="min-h-fit relative flex flex-col">
                  <button
                    type="button"
                    className="vf-method-row"
                    id={option.id}
                    onClick={() =>
                      handleSignInOptionClick(option.value, icons, authLabel)
                    }
                  >
                    {meta ? (
                      <span className={`vf-method-badge ${meta.badgeClass}`}>
                        {t(meta.badgeKey, { defaultValue: meta.badge })}
                      </span>
                    ) : null}
                    <img
                      className="vf-method-icon shrink-0"
                      src={resolveAssetUrl(option.icon)}
                      alt=""
                    />
                    <div className="vf-method-copy min-w-0 flex-1">
                      <div className="vf-method-title">{title}</div>
                      {desc ? (
                        <div className="vf-method-desc">{desc}</div>
                      ) : null}
                    </div>
                    <ChevronRight />
                  </button>
                </div>
              );
            })}
        </div>
      )}

      {showMoreOptions && (
        <div
          className="text-center cursor-pointer font-medium text-[#4095bf] mt-3 flex flex-row rtl:flex-row-reverse items-center justify-center"
          onClick={() => setShowMoreOptions(false)}
          onKeyDown={() => setShowMoreOptions(false)}
          id="show-more-options"
          role="button"
          tabIndex={0}
        >
          <span className="mr-2 rtl:ml-2">{t('more_ways_to_sign_in')}</span>
        </div>
      )}
    </div>
  );
}
